import './record-featured-list.scss'

import { pluck, tap } from 'rxjs/operators'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faFilter, faSortAmountDown, faSortAmountUp, faSpinner } from '@fortawesome/free-solid-svg-icons'

import { RecordFeatured, SearchParams } from '@skinsoft/types'
import { map } from '@skinsoft/data-store/src/operators'
import { RecordDataStore, EntityFieldDataStore } from '@skinsoft/entity'
import { toRecordFeatured$ } from '@skinsoft/entity-ui'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { LocationSelectorPopup } from '@skinsoft/entity-ui/src/location/location-selector-popup'
import { RecordFeaturedCard } from '../record-featured-card'

import { NB_RECORDS_SEARCH_RESULT_CHANGED_TOPIC } from '../../../../busTopics'

const RECORD_FEATURED_ITEM_COUNT = 5;
declare const RECORD_SORTING_ENTITY_FIELDS: any[];

@Component({
    components: {
        sLocationSelectorPopup: LocationSelectorPopup,
        sRecordFeaturedCard: RecordFeaturedCard,
    },
    props: ['searchParams', 'linkedRecordsIds'],
    template: require('./record-featured-list.html')
})
export class RecordFeaturedList extends ReactiveVue {

    public searchParams: SearchParams;
    public linkedRecordsIds: string[]

    public readonly $refs: {
        locationPopup: LocationSelectorPopup,
        resultsList: HTMLDivElement
    };

    private recordFeatureds: RecordFeatured[] = [];
    private totalResults: number = 0;

    private sortingEntityFields: any = {
        linkedItem: '',
        numberIdentifier: ''
    };
    private status: string = "ui.lifecycle-state.inProgress";
    private advancedSearches;

    private $recordDataStore: RecordDataStore;
    private $entityFieldDataStore: EntityFieldDataStore;
    // Icons
    private sortAscIcon = faSortAmountUp;
    private sortDescIcon = faSortAmountDown;
    private recordLocationFilterIcon = faFilter;
    private dotsIcon = faSpinner;

    private recordIdentifierSortIcon = this.sortDescIcon;
    private inventoryNumberSortIcon = this.sortDescIcon;

    private displayInventoryNumberSortIcon: boolean = false;
    private displayRecordRefSortIcon: boolean = false;
    private enableSorting: boolean = true;

    // Infinite scroll properties
    private lastScrollTop: number = 0;
    private isScrollAtTheEnd: boolean = false;
    private nbPagesToLoad: number = 0;
    private loadingNewPage: boolean = false;

    private readonly BUTTON_SLEEP_TIME = 1000;

    public mounted() {
        this.$on("scroll", this.onScrollEvent)
        this.fetchEntityFieldIds()
    }

    private onScrollEvent(e) {
        const currentScrollTop = e.target.scrollTop;
        if (currentScrollTop - this.lastScrollTop > 0) {
            const scrollPositionToEnd = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight;
            if (scrollPositionToEnd < 2 && !this.isScrollAtTheEnd) {
                this.isScrollAtTheEnd = true;
                this.nbPagesToLoad++;
                this.loadNewPage();
            }

            if (scrollPositionToEnd >= 2) {
                this.isScrollAtTheEnd = false;
            }
        }


        this.lastScrollTop = e.target.scrollTop;
    }


    private subscribe(append: boolean) {
        const locale = this.$i18n.locale
        const sortObject =
            this.searchParams.sortType === ''
                ? null
                : { entityFields: [this.searchParams.sortType], sortAsc: this.searchParams.sortAsc };

        const filteredIds = this.linkedRecordsIds;

        let pathAndValueToCheck: any = null
        if(this.status) {
            pathAndValueToCheck = {
                pathToValue: navigator.onLine ? "workflowSchema.currentState" : "workflowSchema.currentState.value",
                type: "String",
                value: this.status
            }
        }

        this.unsubscribe()
        this.subscribeTo(
            this.$recordDataStore
                .searchAll(
                    {
                        queryText: this.searchParams.queryText,
                        sortObject: sortObject,
                        pageSize: this.searchParams.nbResults,
                        types: ["Recolement"],
                        page: this.nbPagesToLoad,
                        advancedSearches: this.advancedSearches,
                        filteredIds: filteredIds,
                    }, 
                    pathAndValueToCheck
                )
                .pipe(
                    tap(results => {
                        this.totalResults = results.totalSize
                    }),
                    pluck('allData'),
                    map((value) => toRecordFeatured$(value, locale))
                )
            ,
            (recordFeatureds: RecordFeatured[]) => {
                if (append) {
                    this.recordFeatureds.push(...recordFeatureds)
                    this.loadingNewPage = false;
                } else {
                    this.recordFeatureds = recordFeatureds;
                }

                this.$nextTick(
                    this.checkResultsAreFilled
                )
                // avoid button spamming which messes with requests
                setTimeout(() => this.enableSorting = true, this.BUTTON_SLEEP_TIME);
                this.emitOnBus(NB_RECORDS_SEARCH_RESULT_CHANGED_TOPIC, { totalResults: this.totalResults })
            }
            , 'recordFeatureds'
        )
    }

    /**
     * Checks whether the results are filling the screen or not.
     * If not, new results from next search page are appended to existing ones
     */
    private checkResultsAreFilled() {
        if (this.$refs.resultsList && this.$refs.resultsList.scrollHeight === this.$refs.resultsList.clientHeight
            && this.totalResults !== this.recordFeatureds.length) {
            this.performSearch(true);
        }
    }

    private async fetchEntityFieldIds() {
        const entityFields = RECORD_SORTING_ENTITY_FIELDS;
        for (const entityField of entityFields) {
            this.sortingEntityFields[entityField.entityFieldName] =
                await this.$entityFieldDataStore.getEntityFieldId(
                    entityField.entityFieldName,
                    entityField.entityName,
                    entityField.schemaName
                )
        }

    }

    private unsubscribe() {
        this.unsubscribeFrom('recordFeatureds');
    }

    private performSearch(append: boolean) {
        if (this.searchParams.nbResults && this.linkedRecordsIds) {
            this.unsubscribe();
            if (!append) {
                this.nbPagesToLoad = 0;
                this.recordFeatureds = []
                this.recordFeatureds = Array(RECORD_FEATURED_ITEM_COUNT).fill(null);
                this.$emit('totalCount', null);
            }
            else {
                this.loadingNewPage = true;
            }
            this.subscribe(append);
        }
    }

    private sortOnInventoryNumber() {
        if (this.enableSorting) {
            this.enableSorting = false;
            if (this.searchParams.sortType === this.sortingEntityFields.linkedItem) {
                this.searchParams.sortAsc = !this.searchParams.sortAsc;
            } else {
                this.searchParams.sortType = this.sortingEntityFields.linkedItem;
            }
        }
    }

    private sortOnRecordIdentifier() {
        if (this.enableSorting) {
            this.enableSorting = false;
            if (this.searchParams.sortType === this.sortingEntityFields.numberIdentifier) {
                this.searchParams.sortAsc = !this.searchParams.sortAsc;
            } else {
                this.searchParams.sortType = this.sortingEntityFields.numberIdentifier;
            }
        }
    }

    private openLocationPopup() {
        this.$refs.locationPopup.open()
    }

    private onLocationValidated(location) {
        this.unsubscribe()
        this.advancedSearches = 
        [
            {
            leftCriteria: {

                entityField: "5d5d3797bbffbb20bbcca5fa",
                operator: "crossed-search",
                type: "criteria",
                value: {
                    indexSearchRequest: {
                        advancedSearches: [
                            {
                                leftCriteria: {
                                    criteriaList: [
                                        {
                                            entityField: "5baccdcb70aa490001802595",
                                            field: "locationItemSchema.realTimeLocation.realTimeLocation",
                                            operator: "equals",
                                            type: "criteria",
                                            value: {
                                                path: `${location.location.locationCS.parentLocation.pathIds}/${location.location.id}`,
                                                refId: location.location.id,
                                                text: location.location.indexTitle,
                                                type: "path"
                                            },
                                        }
                                    ],
                                    type: "nestedSearch"
                                },
                                linkOperator: null,
                                rightCriteria: null,
                                type: "requestSearch"
                            }
                        ],
                        pathAndValueToCheck: null,
                        searchAlternative: false,
                        searchOnArchived: false,
                        searchOnChild: false,
                        searchOnVersioned: false,
                        types: [
                            "numericMuseumItem",
                            "physicalMuseumItem"
                        ],
                    },
                    type: "crossed-search"
                }
            },
            linkOperator: null,
            rightCriteria: null,
            type: "requestSearch"
        }
    ]
        this.subscribe(false)
    }

    @Watch('searchParams', { deep: true })
    private onSearchParamsChange() {
        this.performSearch(false);
    }

    @Watch('linkedRecordsIds')
    private onlinkedRecordsIds() {
        this.performSearch(false);
    }

    @Watch('searchParams', { immediate: true, deep: true })
    private onSortTypeChange() {

        const hasInventoryNumberBeenClicked = this.searchParams.sortType === this.sortingEntityFields.linkedItem;

        this.displayInventoryNumberSortIcon = hasInventoryNumberBeenClicked;
        this.displayRecordRefSortIcon = !hasInventoryNumberBeenClicked;

        const sortIcon = this.searchParams.sortAsc ?
            this.sortAscIcon : this.sortDescIcon;

        this.recordIdentifierSortIcon = sortIcon;
        this.inventoryNumberSortIcon = sortIcon;
    }

    @Watch('status')
    private onStatusChange() {
        this.performSearch(false)
    }

    private loadNewPage() {
        const maxNbpages = Math.floor(this.totalResults / this.searchParams.nbResults) + 1;
        if (this.nbPagesToLoad < maxNbpages) {
            this.performSearch(true);
        }
    }
}